export function enUnidad(unidad: string, larga = false): string {
  switch (unidad) {
    case 't':
      return larga ? 'TONELADA' : 'T';
    case 'm2':
      return 'M²';
    case 'm':
      return larga ? 'METRO LINEAL' : 'M';
    default:
      return 'KG';
  }
}

export interface CategoriaDeGenerador {
  id: number;
  codigo: string;
  nombre: string;

  literal: string;
  porcentajeSbu: number;

  unidad: string;
  recargoPorDistancia: boolean;

  tarifaUnitaria: number;
  generadores: number;
}

export interface Tarifario {
  sbu: number;

  recargoPorKm: number;

  umbralKm: number;
  categorias: CategoriaDeGenerador[];
}

export interface Generador {
  id: number;
  clienteId: number;
  identificacion: string;
  nombre: string;
  nombreComercial: string | null;
  /** El sitio, cuando el RUC tiene varios (el municipio): cada uno se factura aparte. */
  sucursal: string | null;
  /** El código del SIRE, o N-id si no lo tiene: lo que se escribe en la plantilla. */
  codigo: string;
  categoriaId: number;
  categoria: string;
  literal: string;
  recargoPorDistancia: boolean;
  direccionRecoleccion: string | null;
  distanciaKm: number;
  activo: boolean;
  codigoSire: number | null;
  observacion: string | null;
  /** Del catastro: el correo es a donde va su factura electrónica. */
  telefono?: string | null;
  email?: string | null;
}

export type EstadoDelCiclo = 'ABIERTO' | 'CERRADO' | 'FACTURADO';

export interface Ciclo {
  id: number;

  periodo: string;

  sbu: number;
  estado: EstadoDelCiclo;
  abiertoEn: string;
  abiertoPor: string;
  cerradoEn?: string | null;
  cerradoPor?: string | null;
  facturadoEn?: string | null;

  /**
   * Pesajes sin factura. En un ciclo FACTURADO son los que una nota de crédito o una anulación
   * soltó: se corrigen o se borran, y se refacturan.
   */
  sinFacturar: number;

  /** Cuantos pesajes tiene y cuanto suman: lo que se ve en la fila del mes. */
  pesajes: number;
  total: number;

  /** El visto bueno de quien supervisa la emisión: sin él vigente no se factura. */
  revisadoEn?: string | null;
  revisadoPor?: string | null;
  /** Falso si no lo hay o si los pesajes cambiaron después de darlo. */
  vistoBuenoVigente?: boolean;
}

export interface Pesaje {
  id: number;
  generadorId: number;
  generador: string;
  identificacion: string;
  categoriaId: number;
  categoria: string;
  literal: string;

  unidad: string;
  fecha: string;
  kilos: number;
  kmExcedentes: number;
  guia?: string | null;
  tarifaUnitaria: number;
  servicio: number;
  recargo: number;
  total: number;
  observacion?: string | null;
  registradoPor: string;
  facturado: boolean;
  /** El historial que trajo la migracion: el numero de su factura en el sistema anterior, o «SIRE». */
  facturaAnterior?: string;
}

export interface Cotizacion {
  categoriaId: number;
  categoria: string;
  unidad: string;
  recargoPorDistancia: boolean;
  kmExcedentes: number;

  cantidad: number;
  tarifaUnitaria: number;
  servicio: number;
  recargo: number;
  total: number;
}

export interface TotalesDelCiclo {
  pesajes: number;
  generadores: number;
  kilos: number;
  servicio: number;
  recargo: number;
  total: number;
}

export interface ConsolidadoPorGenerador {
  generadorId: number;
  nombre: string;
  identificacion: string;
  categorias: string;
  pesajes: number;
  kilos: number;
  servicio: number;
  recargo: number;
  total: number;
}

export interface Consolidacion {
  ciclo: Ciclo;
  totales: TotalesDelCiclo;
  porGenerador: ConsolidadoPorGenerador[];
  activosSinPesaje: {
    generadorId: number;
    nombre: string;
    identificacion: string;
    categoria: string;
  }[];
}

/** Cómo sale el total de una fila: la cantidad va en la unidad de su tarifa. */
export interface CalculoDeFila {
  categoria: string;
  unidad: string;
  cantidad: number;
  tarifaUnitaria: number;
  servicio: number;
  kmExcedentes: number;
  recargo: number;
}

export interface FilaDePlanilla {
  numero: number;
  ruc?: string;
  generador?: string;
  fecha?: string;
  kilos?: number;
  kmExcedentes?: number;
  guia?: string;
  total?: number;
  motivos: string[];
  calculo?: CalculoDeFila | null;
  /** Lo que no impide registrar la fila, pero conviene mirar. */
  avisos?: string[];
}

export interface GeneradorSinPesaje {
  generadorId: number;
  nombre: string;
  identificacion: string;
  categoria: string;
}

export interface RevisionDePlanilla {
  periodo: string;
  validas: number;
  rechazadas: number;
  conAvisos?: number;
  total: number;
  filas: FilaDePlanilla[];
  /** Activos sin pesaje en el ciclo que tampoco vienen en la planilla. */
  ausentes?: GeneradorSinPesaje[];
}

export interface LineaPrevista {
  concepto: string;
  descripcion: string;
  cantidad: number;
  unidad: string;
  precioUnitario: number;
  valor: number;
}

export interface FacturaPrevista {
  generadorId: number;
  generador: string;
  identificacion: string;
  sucursal: string | null;
  pesajes: number;
  lineas: LineaPrevista[];
  total: number;
}

/** Las facturas tal como saldrían, sin emitir nada. */
export interface VistaPrevia {
  ciclo: Ciclo;
  emision: string;
  vencimiento: string;
  facturas: FacturaPrevista[];
  pesajes: number;
  totalDePesajes: number;
  total: number;
  cuadra: boolean;
}

export interface PlanillaRegistrada {
  registrados: number;
  rechazados: number;
  total: number;
}

export function enTarifa(tarifa: number): string {
  return tarifa.toFixed(4).replace(/0{1,2}$/, '');
}

export interface TipoDeCargo {
  codigo: string;
  nombre: string;

  literal: string;
  concepto: string;
  porcentajeSbu: number;

  unidad: string;

  medida: string;
  sujeto: 'PREDIO' | 'ORGANIZADOR';
  facturacion: 'MENSUAL' | 'INMEDIATA';

  tarifaUnitaria: number;
  activo: boolean;
}

export interface TarifasDeCargo {
  sbu: number;
  tipos: TipoDeCargo[];
}

export interface Predio {
  cuentaId: number;
  numeroCuenta: string;
  cuentaContrato?: string;
  medidor?: string;
  direccion?: string;
  clienteId: number;
  identificacion: string;
  titular: string;
  activo: boolean;
}

export interface CotizacionDeCargo {
  tarifaUnitaria: number;
  unidad: string;

  cantidad: number;
  total: number;
}

export interface Cargo {
  id: number;
  tipo: string;
  tipoNombre: string;
  literal: string;
  unidad: string;
  medida: string;
  clienteId: number;
  identificacion: string;
  nombre: string;
  cuentaId?: number;
  numeroCuenta?: string;
  direccion?: string;
  fecha: string;
  cantidad: number;
  informe: string;
  descripcion: string;
  observacion?: string;
  tarifaUnitaria: number;
  total: number;
  facturaId?: number;
  numeroFactura?: string;
  creadoPor: string;
  creadoEn: string;
}

export interface MesDeAseoPorFacturar {
  periodo: string;
  cargos: number;
  titulares: number;
  total: number;
}
